import type { PayerFileRemittanceClaimGridRow } from '@cumu/shared';
import { currencyFormat } from '@cumu/shared';
import { controller } from '@github/catalyst';
import type AgGrid from 'ag-grid-enterprise';
import {
  ClientSideGridElement,
  ClientSideGridOptions
} from './client-side-grid-element';
import {
  codeDescription,
  dateRange,
  formatUTCDate,
  linkRenderer,
  renderCodeDescription
} from './grid-helpers';

@controller('payer-file-remittance-claims')
class PayerFileRemittanceClaimsElement extends ClientSideGridElement<PayerFileRemittanceClaimGridRow> {
  protected getGridOptions(): ClientSideGridOptions<PayerFileRemittanceClaimGridRow> {
    const columnDefs: AgGrid.ColDef<PayerFileRemittanceClaimGridRow>[] = [
      {
        field: 'submitter_claim_control_number',
        headerName: 'Claim',
        cellRenderer: ({
          data,
          value
        }: AgGrid.ICellRendererParams<PayerFileRemittanceClaimGridRow>) =>
          linkRenderer(data?.claim_href, value),
        width: 135,
        minWidth: 135,
        suppressSizeToFit: true
      },
      {
        field: 'person_full_name',
        headerName: 'Person',
        tooltipField: 'person_full_name',
        flex: 1,
        minWidth: 190
      },
      {
        colId: 'claim_batch',
        headerName: 'Claim batch',
        valueGetter: ({ data }) => claimBatchLabel(data),
        cellRenderer: claimBatchRenderer,
        tooltipValueGetter: ({ data }) => claimBatchTooltip(data),
        wrapText: true,
        autoHeight: true,
        flex: 1,
        minWidth: 230
      },
      {
        colId: 'status',
        headerName: 'Status',
        valueGetter: ({ data }) =>
          codeDescription(
            data?.claim_status_code,
            data?.claim_status_description
          ),
        cellRenderer: ({
          data
        }: AgGrid.ICellRendererParams<PayerFileRemittanceClaimGridRow>) =>
          renderCodeDescription(
            data?.claim_status_code,
            data?.claim_status_description
          ),
        tooltipField: 'claim_status_description',
        wrapText: true,
        autoHeight: true,
        flex: 1,
        minWidth: 230
      },
      {
        field: 'adjudication_date',
        headerName: 'Adjudication',
        valueFormatter: ({ value }) => formatUTCDate(value),
        width: 145
      },
      {
        field: 'submitted_charge_amount',
        headerName: 'Submitted',
        type: 'numericColumn',
        valueFormatter: ({ value }) => formatCurrency(value),
        width: 135
      },
      {
        field: 'paid_amount',
        headerName: 'Paid',
        type: 'numericColumn',
        valueFormatter: ({ value }) => formatCurrency(value),
        width: 125
      },
      {
        field: 'patient_responsibility_amount',
        headerName: 'Patient responsibility',
        type: 'numericColumn',
        valueFormatter: ({ value }) => formatCurrency(value),
        width: 190
      },
      {
        field: 'payer_claim_control_number',
        headerName: 'Payer claim control',
        tooltipField: 'payer_claim_control_number',
        width: 220
      }
    ];

    return {
      getRowId: ({ data }) => data.remittance_claim_id,
      columnDefs
    };
  }
}

function claimBatchLabel(
  row: PayerFileRemittanceClaimGridRow | null | undefined
) {
  if (!row?.claim_batch_id) {
    return 'Unmatched';
  }
  return [
    row.claim_configuration_title ?? 'Claim batch',
    dateRange(row.billing_period_start, row.billing_period_end),
    row.project_login
  ]
    .filter(Boolean)
    .join(' ');
}

function claimBatchRenderer({
  data
}: AgGrid.ICellRendererParams<PayerFileRemittanceClaimGridRow>) {
  if (!data?.claim_batch_id || !data.claim_batch_href) {
    const unmatched = document.createElement('span');
    unmatched.className = 'color-fg-muted';
    unmatched.textContent = 'Unmatched';
    return unmatched;
  }

  const container = document.createElement('span');
  const link = document.createElement('a');
  link.href = data.claim_batch_href;
  link.textContent = data.claim_configuration_title ?? 'View claim batch';
  container.append(link);

  const context = [
    dateRange(data.billing_period_start, data.billing_period_end),
    data.project_login
  ]
    .filter(Boolean)
    .join(' · ');
  if (context) {
    const contextElement = document.createElement('span');
    contextElement.className = 'd-block color-fg-muted';
    contextElement.textContent = context;
    container.append(contextElement);
  }

  return container;
}

function claimBatchTooltip(
  row: PayerFileRemittanceClaimGridRow | null | undefined
) {
  if (!row?.claim_batch_id) {
    return 'No matching Cumulus claim';
  }
  return [
    dateRange(row.billing_period_start, row.billing_period_end),
    row.project_login
  ]
    .filter(Boolean)
    .join(' · ');
}

function formatCurrency(value: unknown) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return currencyFormat.format(Number(value));
}

declare global {
  interface Window {
    PayerFileRemittanceClaimsElement: typeof PayerFileRemittanceClaimsElement;
  }
  interface HTMLElementTagNameMap {
    'payer-file-remittance-claims': PayerFileRemittanceClaimsElement;
  }
}
